import React, { useMemo, useCallback } from 'react';
import { useParams, useLocation } from 'react-router-dom';
import { FormattedMessage } from 'react-intl';
import { useSelector } from 'react-redux';
import classnames from 'classnames';

import { DropdownMenu } from 'ui-components/components/dropdown-menu';
import { PlanFeaturesTrialUpsellBanner } from '../../../../components/plan-features-trial-upsell-banner';
import { BannerSources } from '../../../../components/plan-features-trial-upsell-banner/constants';
import { DropdownMenuType } from 'ui-components/components/dropdown-menu/dropdown-menu-type';
import { DropdownMenuItem } from 'ui-components/components/dropdown-menu-item';
import { DropdownMenuToggler } from 'ui-components/components/dropdown-menu-toggler';
import { LoadingIndicator } from 'ui-components/components/loading-indicator';

import { SHIPSTATION_ANALYTICS_DASHBOARD_FILTER } from '../../../../constants/analytics-events';
import {
  useIsShipStationAnalyticsFeatureEnabled,
  useGetShipStationAnalyticsUserType,
} from '../../../../feature-gates/use-is-shipstation-analytics-feature-enabled';
import { ANALYTICS_VIEW_TYPE } from '../../../../pages/insights/constants';
import InsightsUnavailable from './insights-unavailable';
import messages, { headerMessages, analyticsReportTitles } from './messages';
import { DateRangeDropdown } from '../../../../components/date-range-dropdown';
import DateRangeOptions from '../../../../components/date-range-dropdown-deprecated/date-range-options';
import { MarketplaceIds } from '../../../../enums/marketplace-ids';
import Global from '../../../../store/global-selectors';
import {
  getInsightsOverview,
  getOperationsOverview,
  getEngagementOverview,
  getSalesTrend,
  getCustomersOverview,
  getProductHighlights,
} from '../../api/insights';
import CustomerEngagement from '../../customer-engagement';
import CustomersOverview from '../../customers-overview';
import { insightsPages as InsightsPages } from '../../nav-items';
import Operations from '../../operations';
import Overview from '../../overview';
import { AnalyticsOrdersOverviewReport } from '../../overview/analytics-orders-overview-report';
import ProductHighlights from '../../product-highlights';
import SalesTrends from '../../sales-trends';
import { useInsights } from '../../use-insights';
import Page from '../page';
import { AnalyticsPage } from '../page/analytics-page';
import { AnalyticsProductReport } from '../../product-highlights/analytics-product-report';
import { AnalyticsShipmentOverview } from '../../operations/analytics-shipment-overview-report';
import { AnalyticsSalesAndCustomers } from '../../sales-trends/analytics-sales-and-customers-report';
import { Segment } from '../../../../services/analytics-services/segment';

import style from './style.module.less';
import analyticsStyle from '../../analytics-style.module.less';

const insightsMap = {
  [InsightsPages.overview]: {
    api: getInsightsOverview,
    component: Overview,
    shouldRenderStoreSelector: false,
    section: '#UUID-6a54fcae-d2b7-2146-7ef8-c14fa5bde88b',
  },
  [InsightsPages.operations]: {
    api: getOperationsOverview,
    component: Operations,
    shouldRenderStoreSelector: false,
    section: '#UUID-6c15ab73-9e8a-6366-68b2-ed837a79e03d',
  },
  [InsightsPages.tracking]: {
    api: getEngagementOverview,
    component: CustomerEngagement,
    shouldRenderStoreSelector: true,
    section: '#UUID-0b818b23-42a8-5518-2d7f-e56e83c00839',
  },
  [InsightsPages.sales]: {
    api: getSalesTrend,
    component: SalesTrends,
    shouldRenderStoreSelector: false,
    section: '#UUID-6f6b8c5d-4ac7-1885-8a9c-0532083019aa',
  },
  [InsightsPages.customers]: {
    api: getCustomersOverview,
    component: CustomersOverview,
    shouldRenderStoreSelector: false,
    section: '#UUID-28dcd955-1f1f-7c51-6d36-79f6d64bf96f',
  },
  [InsightsPages.products]: {
    api: getProductHighlights,
    component: ProductHighlights,
    shouldRenderStoreSelector: false,
    section: '#UUID-2e81fbf3-e614-0e17-2300-cc7884018e74',
  },
};

const analyticsMap = {
  [InsightsPages.products]: {
    ...insightsMap[InsightsPages.products],
    component: AnalyticsProductReport,
  },
  [InsightsPages.overview]: {
    ...insightsMap[InsightsPages.overview],
    component: AnalyticsOrdersOverviewReport,
  },
  [InsightsPages.sales]: {
    ...insightsMap[InsightsPages.sales],
    component: AnalyticsSalesAndCustomers,
  },
  [InsightsPages.operations]: {
    ...insightsMap[InsightsPages.operations],
    component: AnalyticsShipmentOverview,
  },
};

const DATE_RANGE_DROPDOWN_ID = 'insights-date-range-dropdown';

const InsightsPageWrapper = () => {
  const { page } = useParams();
  const { pathname } = useLocation();
  const insightsPageKey = page ?? InsightsPages.overview;
  const insightsPage = insightsMap[insightsPageKey];
  const analyticsPage = analyticsMap[insightsPageKey] || insightsPage;
  const isOnOverviewPage = insightsPageKey === InsightsPages.overview;
  const {
    startDateISO,
    endDateISO,
    setDateRangeCallback,
    isLoading,
    selectedStore,
    chartData,
    setSelectedStoreCallback,
    isInsightsEnabled,
  } = useInsights(insightsPage.api);
  const stores = useSelector(Global.selectSellerStores);
  const filteredStores = useMemo(
    () => stores.filter((store) => store.isActive && store.marketplaceId !== MarketplaceIds.AuctaneFulfillment),
    [stores],
  );

  const isShipStationAnalyticsFeatureEnabled = useIsShipStationAnalyticsFeatureEnabled();
  const shipStationAnalyticsSellerType = useGetShipStationAnalyticsUserType() || ANALYTICS_VIEW_TYPE.locked;
  const { chartData: customerChartData } = useInsights(getCustomersOverview);
  const { chartData: customerEngagementChartData } = useInsights(getEngagementOverview);

  const trackFilterChange = useCallback(
    (additionalData = {}) => {
      if (isShipStationAnalyticsFeatureEnabled) {
        const payload = {
          shipStationAnalyticsSellerType,
          location: pathname,
          ...additionalData,
        };

        Segment.trackEvent(SHIPSTATION_ANALYTICS_DASHBOARD_FILTER, payload);
      }
    },
    [isShipStationAnalyticsFeatureEnabled, shipStationAnalyticsSellerType, pathname],
  );

  const handleStoreSelection = useCallback(
    (store) => {
      setSelectedStoreCallback(store);
      trackFilterChange({ selectedStore: store });
    },
    [setSelectedStoreCallback, trackFilterChange],
  );

  const rightAlignedComponent = (
    <div
      className={classnames({
        [style['right-aligned-container']]: !isShipStationAnalyticsFeatureEnabled,
      })}
    >
      {insightsPage.shouldRenderStoreSelector && (
        <div className={style['store-dropdown-wrapper']}>
          <label className={style['dropdown-label']}>
            <FormattedMessage {...messages.storeDropdownLabel} />
          </label>
          <DropdownMenu className={style['store-dropdown']} dropdownMenuShouldHaveWidthOfButton fullWidth variant='form'>
            <DropdownMenuToggler className={style['store-dropdown-toggler']}>
              {selectedStore?.name ?? <FormattedMessage {...messages.allStoresOption} />}
            </DropdownMenuToggler>
            <DropdownMenuItem key='all stores' onClick={() => handleStoreSelection(undefined)} selected={!selectedStore}>
              <FormattedMessage {...messages.allStoresOption} />
            </DropdownMenuItem>
            {filteredStores.map((store) => (
              <DropdownMenuItem
                key={store.storeGuid}
                onClick={() => handleStoreSelection(store)}
                selected={selectedStore?.storeGuid === store.storeGuid}
              >
                {store.name}
              </DropdownMenuItem>
            ))}
          </DropdownMenu>
        </div>
      )}
      <div
        className={classnames(
          {
            [style['date-range-dropdown-wrapper']]: !isShipStationAnalyticsFeatureEnabled,
          },
          {
            [style['filter-bar-wrapper']]: isShipStationAnalyticsFeatureEnabled,
          },
        )}
      >
        <label
          className={classnames(
            {
              [style['dropdown-label']]: !isShipStationAnalyticsFeatureEnabled,
            },
            {
              [style['filter-bar-label']]: isShipStationAnalyticsFeatureEnabled,
            },
          )}
          htmlFor={DATE_RANGE_DROPDOWN_ID}
        >
          <FormattedMessage {...messages.dateRangeDropdownLabel} />
        </label>
        <DateRangeDropdown
          buttonSize='large'
          className={style['date-range-dropdown']}
          dateRangeOptions={DateRangeOptions}
          dropdownMenuType={DropdownMenuType.ComboBox}
          dropdownTogglerClassname={classnames({
            [analyticsStyle['analytics-date-range-toggler']]: isShipStationAnalyticsFeatureEnabled,
          })}
          endDate={endDateISO}
          id={DATE_RANGE_DROPDOWN_ID}
          onApplyCallback={trackFilterChange}
          onDateRangeUpdated={setDateRangeCallback}
          startDate={startDateISO}
        />
      </div>
    </div>
  );

  const shouldRenderInsightsPage = !isLoading && isInsightsEnabled;

  let analyticsChartData = chartData;
  if (isShipStationAnalyticsFeatureEnabled && insightsPageKey === InsightsPages.sales) {
    analyticsChartData = {
      ...chartData,
      ...customerChartData,
      ...customerEngagementChartData,
    };
  }

  return isShipStationAnalyticsFeatureEnabled ? (
    <AnalyticsPage filterBar={rightAlignedComponent} pageTitle={analyticsReportTitles[insightsPageKey]}>
      {isOnOverviewPage && (
        <PlanFeaturesTrialUpsellBanner className={style['plan-features-trial-upsell-banner']} source={BannerSources.Analytics} />
      )}
      {isLoading && <LoadingIndicator />}
      {!isLoading && <analyticsPage.component chartData={analyticsChartData} selectedStore={selectedStore} />}
    </AnalyticsPage>
  ) : (
    <Page
      messages={headerMessages[insightsPageKey]}
      rightAlignedComponent={shouldRenderInsightsPage ? rightAlignedComponent : ''}
      sectionLink={insightsPage.section}
      shouldShowHelperLink
    >
      {isOnOverviewPage && (
        <PlanFeaturesTrialUpsellBanner className={style['plan-features-trial-upsell-banner']} source={BannerSources.Analytics} />
      )}
      {isLoading && <LoadingIndicator />}
      {!shouldRenderInsightsPage && <InsightsUnavailable />}
      {shouldRenderInsightsPage && <insightsPage.component chartData={chartData} selectedStore={selectedStore} />}
    </Page>
  );
};

export default InsightsPageWrapper;
